import { useMemo } from 'react';
import { ROOTS, paths } from 'src/routes/paths';
import { useLocales } from 'src/locales';
import { useGetWorkspacesQuery } from 'src/context/api/workspaces';
import { useAuth } from 'src/auth';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import Iconify from 'src/components/iconify';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';

const icon = (name: string, flip?: 'horizontal' | 'vertical') => (
  <Iconify icon={name} sx={{ width: 2, height: 2 }} flip={flip} />
);

export function useNavData() {
  const { t } = useLocales();
  const workspaces = useGetWorkspacesQuery(null);
  const { user } = useAuth();
  const workspace = useCurrentWorkspace();
  const demoMode = useSelector((state: any) => state.filters.demoMode);
  const role = user?.role;
  const isInternalRole = ['admin', 'brand specialist', 'consultant'].includes(role);
  const isLead = workspace?.template && workspace.template.trim().toLowerCase() === 'lead';
  const theme = useTheme();
  const filterStore = useSelector((state: any) => state.filters);
  const isMonoCountry = filterStore?.loadedCountries?.length === 1;

  const data = useMemo(() => {
    const ICONS = {
      searchPerformance: icon('uil:credit-card-search'),
      visibility: icon('tabler:users'),
      campaignLeaks: icon('mdi:announcement-outline'),
      // Optimization Hub
      optimizationHub: icon('fluent:top-speed-20-regular'),
      // Non Brand
      nonBrandSearchPerformance: icon('uil:credit-card-search'),
      nonBrandVisibility: icon('tabler:users'),
      conquestMode: icon('mingcute:target-line'),
      // Competitors
      trackedCompetitors: icon('hugeicons:sword-01'),
      leaderboard: icon('ph:flag-banner-fold', 'horizontal'),
    };
    const dashboardSection = {
      subheader: 'Brand',
      items: [
        {
          title: 'Search Performance',
          path: paths.dash.searchPerformanceOverview,
          icon: ICONS.searchPerformance,
          activePaths: [
            paths.dash.searchPerformanceOverview,
            paths.dash.searchPerformanceConversionsAnalysis,
            paths.dash.searchPerformanceFunnel,
            paths.dash.searchPerformanceExplore,
            paths.dash.searchPerformanceClusterManager,
          ],
        },
        {
          title: 'Visibility',
          path: paths.dash.visibilityOverview,
          icon: ICONS.visibility,
          activePaths: [
            paths.dash.visibilityOverview,
            paths.dash.visibilityCompetitors,
            paths.dash.visibilitySearchTerms,
            paths.dash.visibilityAds,
            // Without this, the sidebar loses the Visibility highlight on the Local tab.
            paths.dash.visibilityLocal,
          ],
        },
        {
          title: 'Campaign Leaks',
          path: paths.dash.campaignLeaks,
          icon: ICONS.campaignLeaks,
          lastChildren: demoMode || !['admin', 'brand specialist'].includes(role),
        },
        ...(!demoMode && ['admin', 'brand specialist'].includes(role)
          ? [{
            title: 'Brand Shield',
            icon: icon('material-symbols:shield-outline'),
            path: paths.dash.brandShieldKeywordsExact,
            locked: true,
            lastChildren: true,
            activePaths: [
              paths.dash.brandShieldKeywordsExact,
              paths.dash.brandShieldKeywordsOther,
              paths.dash.brandShieldCampaigns,
              paths.dash.activeMode,
              paths.dash.koBiddingMaxCPC,
              paths.dash.koBiddingTROAS,
              paths.dash.koBiddingTCPA,
              paths.dash.koBiddingTargetImpressionShare,
              paths.dash.koBiddingAuto,
              paths.dash.koBiddingCampaignBased,
              paths.dash.koBiddingAdGroupBased,
              paths.dash.scraping,
            ],
          }]
          : []
        ),

      ],
    };

    const adminSection: any[] = [];

    const brandPulseSection = [
      ...(
        (
          (workspace?.brandpulse && workspace?.brandpulse_visibility?.includes(role)) ||
          (workspace?.conquestmode && workspace?.conquestmode_visibility?.includes(role))
        )
          ? [{
            subheader: `Competitors`,
            locked: !workspace?.brandpulse_visibility?.includes("customer"),
            items: [
              ...(workspace?.brandpulse && workspace?.brandpulse_visibility?.includes(role)
                ? [{
                  title: 'Leaderboard',
                  path: paths.dash.leaderboardMarketLeaders,
                  icon: ICONS.leaderboard,
                  activePaths: [
                    paths.dash.leaderboardMarketLeaders,
                    paths.dash.leaderboardTrackedCompetitors,
                    paths.dash.leaderboardCompetitorsSelection,
                  ],
                }]
                : []),

              ...(workspace?.conquestmode && workspace?.conquestmode_visibility?.includes(role)
                ? [{
                  title: 'Conquest Mode',
                  path: paths.dash.conquestMode,
                  icon: ICONS.conquestMode,
                }]
                : []),
            ],
          }]
          : []
      ),
    ];


    const nonBrandItems = [
      ...(workspace?.nonbrand && (isInternalRole || workspace?.nonbrand_visibility?.includes(role))
        ? [
          { title: 'Search Performance', path: paths.dash.nonBrandSearchPerformance, icon: ICONS.nonBrandSearchPerformance },
          { title: 'Visibility', path: paths.dash.nonBrandVisibility, icon: ICONS.nonBrandVisibility },
        ]
        : []),
    ];

    const nonBrandSection = nonBrandItems.length > 0
      ? [
        {
          subheader: `Non Brand`,
          locked: !workspace?.nonbrand_visibility?.includes("customer"),
          items: nonBrandItems,
        },
      ]
      : [];

    return [dashboardSection, ...nonBrandSection, ...brandPulseSection, ...adminSection];
  }, [
    t,
    workspaces.data,
    role,
    workspace?.template,
    isLead,
    demoMode,
    theme.palette.mode,
    isMonoCountry,
    workspace
  ]);

  return data;
}
